import { useState, useEffect, useCallback } from 'react';
import { 
  getSessionTypesByListingApi, 
  getCampByIdApi, 
  getSchedulingBlocksApi,
  getSchedulededDatesApi 
} from '../../../util/api';
import { createFormattedDate } from '../../ProviderPage/utils/dateUtils';

/**
 * Custom hook to fetch and manage session types for a listing
 * Also fetches related availability data (camps, blocks, scheduled dates)
 */
const monthKeyFromDate = viewedMonth => {
  if (!viewedMonth || !(viewedMonth instanceof Date)) {
    return null;
  }
  return `${viewedMonth.getFullYear()}-${viewedMonth.getMonth()}`;
};

export const useListingSessionTypes = (listingId, viewedMonth = null, { enabled = true } = {}) => {
  const [sessionTypes, setSessionTypes] = useState([]);
  const [availabilityData, setAvailabilityData] = useState({
    camps: {},
    schedulingBlocks: {},
    scheduledDates: []
  });
  const [coachTimezone, setCoachTimezone] = useState(null);
  const [listingGuestRequirements, setListingGuestRequirements] = useState('');
  const [listingAccessibility, setListingAccessibility] = useState('');
  const [listingDescription, setListingDescription] = useState('');
  const [listingTitle, setListingTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const viewedMonthKey = monthKeyFromDate(viewedMonth);

  const loadSessionTypes = useCallback(async () => {
    if (!enabled || !listingId) {
      if (!listingId) {
        setSessionTypes([]);
        setCoachTimezone(null);
        setListingGuestRequirements('');
        setListingAccessibility('');
        setListingDescription('');
        setListingTitle('');
      }
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch session types for this listing
      const response = await getSessionTypesByListingApi({ listingId });
      const sessionTypesData = response?.data || [];
      setCoachTimezone(response?.coachTimezone || null);
      setListingGuestRequirements(response?.listingGuestRequirements || '');
      setListingAccessibility(response?.listingAccessibility || '');
      setListingDescription(response?.listingDescription || '');
      setListingTitle(response?.listingTitle || '');
      
      // Sort by sortOrder (ascending), then by createdAt (descending) as fallback
      const sortedSessionTypes = [...sessionTypesData].sort((a, b) => {
        const sortOrderA = a.sortOrder ?? 0;
        const sortOrderB = b.sortOrder ?? 0;
        
        if (sortOrderA !== sortOrderB) {
          return sortOrderA - sortOrderB;
        }
        
        // If sortOrder is the same, sort by createdAt (newer first)
        const createdAtA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const createdAtB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return createdAtB - createdAtA;
      });
      
      setSessionTypes(sortedSessionTypes);

      // Fetch related availability data
      // If viewedMonth is provided, we can optimize the fetch
      let dateRange = {};
      if (viewedMonth) {
        const year = viewedMonth.getFullYear();
        const month = viewedMonth.getMonth();
        const lastDay = new Date(year, month + 1, 0).getDate();
        dateRange = {
          startDate: createFormattedDate(year, month, 1),
          endDate: createFormattedDate(year, month, lastDay),
        };
      }

      await loadAvailabilityData(sessionTypesData, dateRange);

    } catch (err) {
      console.error('Error loading session types:', err);
      setError('Failed to load session types');
      setSessionTypes([]);
    } finally {
      setLoading(false);
    }
  }, [enabled, listingId, viewedMonthKey, viewedMonth]);

  const loadAvailabilityData = async (sessionTypesData, dateRange = {}) => {
    try {
      // Get unique camp IDs and availability IDs
      const campIds = [...new Set(
        sessionTypesData
          .filter(st => st.isCampSession && st.campId)
          .map(st => st.campId)
      )];

      const availabilityIds = [...new Set(
        sessionTypesData
          .filter(st => !st.isCampSession && st.availabilityId)
          .map(st => st.availabilityId)
      )];

      // Fetch camps - get userId from first session type (all session types for a listing have the same userId)
      const listingOwnerUserId = sessionTypesData.length > 0 ? sessionTypesData[0].userId : null;
      const campsMap = {};
      
      if (campIds.length > 0 && listingOwnerUserId) {
        await Promise.all(
          campIds.map(async (campId) => {
            try {
              // Pass userId to fetch camp for the listing owner
              const campResponse = await getCampByIdApi({ campId, userId: listingOwnerUserId });
              if (campResponse) {
                campsMap[campId] = campResponse;
              }
            } catch (err) {
              console.error(`Error loading camp ${campId}:`, err);
            }
          })
        );
      }
      
      console.log('Loaded camps:', Object.keys(campsMap).length, Object.keys(campsMap).map(id => ({ campId: id })));
      console.log('Camp IDs requested:', campIds);

      // Fetch scheduling blocks and scheduled dates for non-camp sessions
      // Always load these if there are any non-camp session types
      let schedulingBlocks = {};
      let scheduledDates = [];
      
      const hasNonCampSessions = sessionTypesData.some(st => !st.isCampSession);
      
      if (hasNonCampSessions) {
        try {
          // Get userId from first session type (all session types for a listing have the same userId)
          const listingOwnerUserId = sessionTypesData.length > 0 ? sessionTypesData[0].userId : null;
          
          const [blocksResponse, datesResponse] = await Promise.all([
            // Pass userId to fetch scheduling blocks for the listing owner
            getSchedulingBlocksApi(listingOwnerUserId ? { userId: listingOwnerUserId } : {}),
            // Pass userId and date range to fetch scheduled dates for the listing owner
            getSchedulededDatesApi({
              ...(listingOwnerUserId ? { userId: listingOwnerUserId } : {}),
              ...dateRange
            })
          ]);

          const blocks = Array.isArray(blocksResponse) ? blocksResponse : [];
          scheduledDates = Array.isArray(datesResponse) ? datesResponse : [];

          // Create blocks map - blocks are already filtered by userId on the backend
          blocks.forEach(block => {
            schedulingBlocks[block._id] = block;
          });

          console.log('Loaded scheduling blocks:', blocks.length, blocks.map(b => ({ id: b._id, name: b.name, type: b.type })));
          console.log('Loaded scheduled dates for userId:', listingOwnerUserId, scheduledDates.length, scheduledDates.map(sd => ({ blockId: sd.blockId, date: sd.date, blockName: sd.blockName })));
          console.log('Session types availability IDs:', sessionTypesData.map(st => ({ name: st.sessionName, availabilityId: st.availabilityId })));
        } catch (err) {
          console.error('Error loading availability blocks:', err);
        }
      }

      setAvailabilityData({
        camps: campsMap,
        schedulingBlocks,
        scheduledDates
      });

    } catch (err) {
      console.error('Error loading availability data:', err);
    }
  };

  useEffect(() => {
    loadSessionTypes();
  }, [loadSessionTypes]);

  return {
    sessionTypes,
    availabilityData,
    coachTimezone,
    listingGuestRequirements,
    listingAccessibility,
    listingDescription,
    listingTitle,
    loading,
    error,
    reload: loadSessionTypes
  };
};
